import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

const SITE_URL = "https://legacylensai.com";

type Props = {
  slug: string;
  fullName: string;
  dateLabel?: string | null;
};

const PrintableQRCard = ({ slug, fullName, dateLabel }: Props) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const years = dateLabel ?? null;

  const tributeUrl = `${SITE_URL}/tribute/${slug}`;

  const handlePrint = () => {
    const card = cardRef.current;
    if (!card) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const canvas = card.querySelector("canvas");
    const qrDataUrl = canvas?.toDataURL("image/png") || "";

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Card - ${fullName}</title>
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500&display=swap" rel="stylesheet">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: 3.5in 5in; margin: 0; }
          body {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #f5f5f5;
            font-family: 'Inter', sans-serif;
          }
          .card {
            width: 3.5in;
            height: 5in;
            background: #fff;
            border: 1px solid #e0e0e0;
            border-radius: 12px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 0.4in 0.3in;
            text-align: center;
            gap: 0;
            position: relative;
            overflow: hidden;
          }
          .card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, #b8a88a, #d4c5a9, #b8a88a);
          }
          .name {
            font-family: 'Playfair Display', serif;
            font-size: 18px;
            font-weight: 700;
            color: #1a1d23;
            line-height: 1.2;
            margin-bottom: 4px;
          }
          .years {
            font-family: 'Playfair Display', serif;
            font-size: 13px;
            color: #6b7280;
            letter-spacing: 0.1em;
            margin-bottom: 16px;
          }
          .divider {
            width: 40px;
            height: 1px;
            background: #d4c5a9;
            margin-bottom: 16px;
          }
          .qr {
            margin-bottom: 16px;
          }
          .qr img {
            width: 160px;
            height: 160px;
          }
          .instruction {
            font-size: 10px;
            color: #9ca3af;
            line-height: 1.4;
            margin-bottom: 12px;
          }
          .url {
            font-size: 8px;
            color: #b8a88a;
            word-break: break-all;
            margin-bottom: 8px;
          }
          .branding {
            font-size: 8px;
            color: #d1d5db;
            letter-spacing: 0.05em;
          }
          @media print {
            body { background: none; min-height: auto; }
            .card { border: none; border-radius: 0; box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="name">${fullName}</div>
          ${years ? `<div class="years">${years}</div>` : '<div style="margin-bottom:16px"></div>'}
          <div class="divider"></div>
          <div class="qr"><img src="${qrDataUrl}" alt="QR Code" /></div>
          <div class="instruction">Scan to visit the tribute</div>
          <div class="url">${tributeUrl}</div>
          <div class="branding">LegacyLens AI</div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <div>
      {/* Preview card */}
      <div
        ref={cardRef}
        className="mx-auto bg-white rounded-xl border border-border/50 flex flex-col items-center justify-center text-center px-8 py-10 relative overflow-hidden"
        style={{ width: "280px" }}
      >
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#b8a88a] via-[#d4c5a9] to-[#b8a88a]" />
        <h2 className="font-display text-lg font-bold text-[#1a1d23] leading-tight mb-1">
          {fullName}
        </h2>
        {years && (
          <p className="font-display text-sm text-[#6b7280] tracking-wider mb-4">{years}</p>
        )}
        {!years && <div className="mb-4" />}
        <div className="w-10 h-px bg-[#d4c5a9] mb-4" />
        <QRCodeCanvas
          value={tributeUrl}
          size={140}
          level="H"
          includeMargin
          className="mb-4"
        />
        <p className="text-[10px] text-[#9ca3af] mb-2">Scan to visit the tribute</p>
        <p className="text-[8px] text-[#b8a88a] break-all mb-2">{tributeUrl}</p>
        <p className="text-[8px] text-[#d1d5db] tracking-wide">LegacyLens AI</p>
      </div>

      <div className="flex justify-center mt-4">
        <Button size="sm" onClick={handlePrint}>
          <Printer className="w-4 h-4 mr-2" /> Print Card
        </Button>
      </div>
    </div>
  );
};

export default PrintableQRCard;
